import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { employeesRepository } from "@/repositories/employees";
import { subscriptionsRepository } from "@/repositories/subscriptions";
import type { Subscription, SubscriptionPlan, SubscriptionPricingTier, PricingSetting } from "@/types/subscription";

/**
 * Hook to fetch the current company's subscription
 */
export function useSubscription() {
  const { companyId } = useAuth();

  return useQuery({
    queryKey: ["subscription", companyId],
    queryFn: async () =>
      (await subscriptionsRepository.getFullByCompany(companyId!)) as Subscription | null,
    enabled: !!companyId,
  });
}

/**
 * Hook to fetch all active subscription plans
 */
export function useSubscriptionPlans() {
  return useQuery({
    queryKey: ["subscription-plans"],
    queryFn: async () =>
      (await subscriptionsRepository.listActivePlans()) as SubscriptionPlan[],
  });
}

/**
 * Hook to fetch pricing tiers for a specific plan
 */
export function usePricingTiers(planId?: string) {
  return useQuery({
    queryKey: ["pricing-tiers", planId],
    queryFn: async () =>
      (await subscriptionsRepository.listPricingTiers(planId)) as SubscriptionPricingTier[],
    enabled: !planId || !!planId,
  });
}

/**
 * Hook to fetch pricing settings (Super Admin only)
 */
export function usePricingSettings() {
  return useQuery({
    queryKey: ["pricing-settings"],
    queryFn: async () =>
      (await subscriptionsRepository.listPricingSettings()) as PricingSetting[],
  });
}

/**
 * Calculate price based on unit/user count and pricing tiers
 */
export function calculatePrice(
  unitCount: number,
  userCount: number,
  unitTiers: SubscriptionPricingTier[],
  userTiers: SubscriptionPricingTier[],
  billingCycle: "monthly" | "annual",
  annualDiscountPercent: number
): { monthly: number; annual: number; final: number } {
  // Calculate unit price
  let unitPrice = 0;
  for (const tier of unitTiers) {
    if (unitCount >= tier.min_count) {
      if (tier.max_count === null || unitCount <= tier.max_count) {
        unitPrice = unitCount * tier.price_per_unit;
        break;
      }
    }
  }

  // Calculate user price
  let userPrice = 0;
  for (const tier of userTiers) {
    if (userCount >= tier.min_count) {
      if (tier.max_count === null || userCount <= tier.max_count) {
        userPrice = userCount * tier.price_per_unit;
        break;
      }
    }
  }

  const monthlyPrice = unitPrice + userPrice;
  const annualPrice = monthlyPrice * 12 * (1 - annualDiscountPercent / 100);
  const finalPrice = billingCycle === "annual" ? annualPrice : monthlyPrice;

  return {
    monthly: monthlyPrice,
    annual: annualPrice,
    final: finalPrice,
  };
}

/**
 * Hook to check subscription limits.
 *
 * DEPRECATED: Subscription limits were enforced for self_admin role only.
 * self_admin retired March 2026. Limits always return Infinity until
 * subscription enforcement is rebuilt for the new role model.
 */
export function useSubscriptionLimits() {
  const { companyId } = useAuth();
  const { data: subscription } = useSubscription();

  const checkUnitLimit = async (_countToAdd: number = 1): Promise<{
    canAdd: boolean;
    current: number;
    limit: number;
    wouldExceedBy: number;
  }> => {
    return { canAdd: true, current: 0, limit: Infinity, wouldExceedBy: 0 };
  };

  const checkUserLimit = async (_countToAdd: number = 1): Promise<{
    canAdd: boolean;
    current: number;
    limit: number;
    wouldExceedBy: number;
  }> => {
    return { canAdd: true, current: 0, limit: Infinity, wouldExceedBy: 0 };
  };

  // Suppress unused variable warning — companyId kept for future limit enforcement
  void companyId;

  return {
    subscription,
    unitLimit: Infinity,
    userLimit: Infinity,
    checkUnitLimit,
    checkUserLimit,
  };
}

/**
 * Hook to get current unit usage count (reactive).
 * DEPRECATED: was only enabled for self_admin. Query disabled until
 * subscription enforcement is rebuilt for the new role model.
 */
export function useUnitUsage() {
  const { companyId } = useAuth();

  return useQuery({
    queryKey: ["unit-usage", companyId],
    queryFn: async () => {
      const { count, error } = await supabase
        .from("units")
        .select("id", { count: "exact", head: true })
        .eq("company_id", companyId!);

      if (error) throw error;
      return count ?? 0;
    },
    enabled: false,
  });
}

/**
 * Hook to get current employee usage count (reactive).
 * DEPRECATED: was only enabled for self_admin. Query disabled until
 * subscription enforcement is rebuilt for the new role model.
 */
export function useEmployeeUsage() {
  const { companyId } = useAuth();

  return useQuery({
    queryKey: ["employee-usage", companyId],
    queryFn: () => employeesRepository.count(companyId!, false),
    enabled: false,
  });
}
